<template>
  <div @click="checkOutside(!showSelector)">
    <el-popover
      v-model:visible="showSelector"
      :disabled="disabled"
      placement="top-end"
      :width="350"
    >
      <div v-if="showSelector" class="tw-rounded-xl tw-bg-white tw-z-50">
        <div class="emoji-container tw-overflow-auto">
          <template v-if="filteredEmojiCategories.length">
            <div
              v-for="category in filteredEmojiCategories"
              :key="category.name"
            >
              <div
                v-if="showCategoryTitle"
                class="tw-text-left tw-text-gray-400 tw-font-light tw-text-sm tw-mb-2 tw-mt-1"
              >
                {{ t(`emoji.${category.name}`) }}
              </div>

              <div class="tw-flex tw-flex-wrap">
                <AppButton
                  v-for="emoji in category.emojis"
                  :id="'emoji-picker-' + emoji + '_btn_id'"
                  :key="emoji"
                  class="emoji tw-m-2 tw-px-1 tw-text-4xl tw-py-0 tw-border-0"
                  @click="$emit('emoji-select', emoji)"
                >
                  <span :class="emojiStyle">{{ emoji }}</span>
                </AppButton>
              </div>
            </div>
          </template>

          <div
            v-else
            class="tw-text-center tw-text-gray-400 tw-font-light tw-text-sm tw-mt-2"
          >
            {{ t('chat.labels.noEmoji') }}
          </div>
        </div>
      </div>
      <template #reference>
        <div @click="showSelector = !showSelector">
          <slot />
        </div>
      </template>
    </el-popover>
  </div>
</template>

<script lang="ts">
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'
import startsWith from 'lodash/startsWith'
import { computed, defineComponent, PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useEmojis } from './composables'

type SizeTypes = 'default' | 'small' | 'large'

const emojiStyles: Record<SizeTypes, string> = {
  default: 'tw-text-2xl',
  small: 'tw-text-lg',
  large: 'tw-text-4xl',
} as const

export default defineComponent({
  props: {
    showCategoryTitle: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String as PropType<SizeTypes>,
      default: 'large',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['emoji-select'],

  setup(props) {
    const { emojiCategories } = useEmojis()
    const filteredEmojiCategories = ref(emojiCategories)

    const showSelector = ref(false)
    const search = ref('')

    const { t } = useI18n()

    const emojiStyle = computed(() => {
      return emojiStyles[props.size]
    })
    const checkOutside = (val: boolean) => {
      showSelector.value = val
    }
    const onSearch = (val: string) => {
      if (val) {
        filteredEmojiCategories.value = emojiCategories
          .map((category) => ({
            name: category.name,
            emojis: pickBy(category.emojis, (val, key) =>
              startsWith(key, search.value)
            ),
          }))
          .filter((category) => Object.keys(category.emojis).length > 0)
      } else {
        filteredEmojiCategories.value = emojiCategories
      }
    }

    watch(search, debounce(onSearch, 300))

    return {
      filteredEmojiCategories,

      showSelector,
      search,
      checkOutside,

      emojiStyle,
      t,
    }
  },
})
</script>

<style lang="scss" scoped>
.emoji-container {
  height: 200px;
  width: 330px;
}
</style>
